<template>
  <div
    v-if="visible"
    class="waajo-ticket-edit-modal-backdrop"
    @click.self="close"
  >
    <div class="waajo-ticket-edit-modal" role="dialog" aria-modal="true" aria-label="Ubah Tiket">
      <div class="waajo-ticket-edit-modal-head">
        <div>
          <h4><i class="fa fa-pencil"></i> Ubah Tiket</h4>
          <p>Status tiket tidak diubah dari form ini. Gunakan Kanban atau kontrol status.</p>
        </div>
        <button type="button" class="waajo-ticket-edit-modal-close" :disabled="saving" @click="close">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <form class="waajo-ticket-edit-modal-body" @submit.prevent="save">
        <div class="waajo-ticket-form-group">
          <label>Judul Tiket <em>*</em></label>
          <input v-model.trim="form.title" type="text" maxlength="255" :disabled="saving">
        </div>

        <div class="waajo-ticket-form-grid">
          <div class="waajo-ticket-form-group">
            <label>Kategori <em>*</em></label>
            <select v-model="form.category" :disabled="saving">
              <option v-for="item in categories" :key="item.value" :value="item.value">{{ item.label }}</option>
            </select>
          </div>
          <div class="waajo-ticket-form-group">
            <label>Prioritas <em>*</em></label>
            <select v-model="form.priority" :disabled="saving">
              <option v-for="item in priorities" :key="item.value" :value="item.value">{{ item.label }}</option>
            </select>
          </div>
        </div>

        <div class="waajo-ticket-form-group">
          <label>Deskripsi <em>*</em></label>
          <textarea v-model.trim="form.description" rows="6" maxlength="10000" :disabled="saving"></textarea>
        </div>

        <div class="waajo-ticket-form-group">
          <label>Jadwal Tindak Lanjut</label>
          <input v-model="form.follow_up_local" type="datetime-local" :disabled="saving">
          <small>Kosongkan jika tiket tidak memiliki jadwal tindak lanjut.</small>
        </div>

        <div v-if="error" class="waajo-ticket-form-error">
          <i class="fa fa-exclamation-circle"></i> {{ error }}
        </div>

        <div class="waajo-ticket-edit-modal-footer">
          <button type="button" class="waajo-ticket-btn" :disabled="saving" @click="close">Batal</button>
          <button type="submit" class="waajo-ticket-btn is-primary" :disabled="saving">
            <i v-if="saving" class="fa fa-spinner fa-spin"></i>
            {{ saving ? 'Menyimpan...' : 'Simpan Perubahan' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  toDatetimeLocal,
  datetimeLocalToUnix,
  extractApiErrorMessage
} from '@/utils/ticket'

export default {
  name: 'TicketEditModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    ticket: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      categories: TICKET_CATEGORIES,
      priorities: TICKET_PRIORITIES,
      form: {
        title: '',
        category: '',
        priority: 'normal',
        description: '',
        follow_up_local: ''
      },
      saving: false,
      error: ''
    }
  },
  watch: {
    visible(value) {
      if (value) this.initialize()
    }
  },
  methods: {
    initialize() {
      const ticket = this.ticket || {}
      this.error = ''
      this.saving = false
      this.form = {
        title: String(ticket.title || ''),
        category: String(ticket.category || ''),
        priority: String(ticket.priority || 'normal'),
        description: String(ticket.description || ''),
        follow_up_local: toDatetimeLocal(ticket.follow_up_datetime)
      }
    },
    validate() {
      if (!this.form.title) return 'Judul tiket wajib diisi.'
      if (!this.form.category) return 'Kategori wajib dipilih.'
      if (!this.form.priority) return 'Prioritas wajib dipilih.'
      if (!this.form.description) return 'Deskripsi tiket wajib diisi.'
      return ''
    },
    close() {
      if (this.saving) return
      this.$emit('close')
    },
    async save() {
      if (this.saving) return
      if (!this.ticket || !this.ticket.id) {
        this.error = 'Detail tiket belum tersedia.'
        return
      }
      const validationError = this.validate()
      if (validationError) {
        this.error = validationError
        return
      }
      const followUpDatetime = datetimeLocalToUnix(this.form.follow_up_local)
      if (this.form.follow_up_local && !followUpDatetime) {
        this.error = 'Jadwal tindak lanjut tidak valid.'
        return
      }

      this.saving = true
      this.error = ''
      try {
        const response = await this.$store.dispatch('ticket/update', {
          id: this.ticket.id,
          title: this.form.title,
          description: this.form.description,
          category: this.form.category,
          priority: this.form.priority,
          follow_up_datetime: followUpDatetime
        })
        this.$emit('saved', response && response.data ? response.data : this.ticket)
      } catch (apiError) {
        this.error = extractApiErrorMessage(apiError, 'Gagal memperbarui tiket.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.waajo-ticket-edit-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 28px 18px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.62);
}
.waajo-ticket-edit-modal {
  width: 100%;
  max-width: 640px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}
.waajo-ticket-edit-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 24px 15px;
  border-bottom: 1px solid var(--waajo-border);
}
.waajo-ticket-edit-modal-head h4 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: var(--waajo-text);
}
.waajo-ticket-edit-modal-head h4 i {
  color: var(--waajo-brand);
  margin-right: 7px;
}
.waajo-ticket-edit-modal-head p {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--waajo-text-muted);
}
.waajo-ticket-edit-modal-close {
  border: 0;
  background: transparent;
  color: var(--waajo-text-muted);
  font-size: 18px;
  line-height: 1;
}
.waajo-ticket-edit-modal-body {
  padding: 18px 24px 22px;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
}
.waajo-ticket-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.waajo-ticket-form-group {
  margin-bottom: 14px;
}
.waajo-ticket-form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--waajo-text-body);
  margin-bottom: 7px;
}
.waajo-ticket-form-group label em {
  color: #dc2626;
  font-style: normal;
}
.waajo-ticket-form-group input,
.waajo-ticket-form-group select,
.waajo-ticket-form-group textarea {
  width: 100%;
  min-height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--waajo-text);
  background: #fff;
  outline: none;
}
.waajo-ticket-form-group textarea {
  resize: vertical;
  min-height: 120px;
}
.waajo-ticket-form-group input:focus,
.waajo-ticket-form-group select:focus,
.waajo-ticket-form-group textarea:focus {
  border-color: var(--waajo-brand-border-strong);
  box-shadow: 0 0 0 3px var(--waajo-focus-ring);
}
.waajo-ticket-form-group small {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--waajo-text-muted);
}
.waajo-ticket-form-error {
  padding: 11px 13px;
  margin-top: 4px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.5;
}
.waajo-ticket-edit-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  margin-top: 6px;
  border-top: 1px solid var(--waajo-border);
}
.waajo-ticket-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid #cbd5e1;
  border-radius: 9999px;
  background: #fff;
  color: var(--waajo-text-body);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}
.waajo-ticket-btn.is-primary {
  border-color: var(--waajo-brand);
  background: var(--waajo-brand);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 7px 16px rgba(243, 156, 18, 0.24);
}
.waajo-ticket-btn:disabled {
  opacity: 0.6;
}
@media (max-width: 640px) {
  .waajo-ticket-edit-modal-backdrop {
    padding: 10px;
  }
  .waajo-ticket-form-grid {
    grid-template-columns: 1fr;
  }
  .waajo-ticket-edit-modal-body {
    padding: 14px 16px 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketEditModal.vue